import React, { useCallback, useEffect, useState } from 'react';
import { DriveFolder, ensureGoogleDriveAuth, listDriveFolders } from '../services/googleDriveService';

interface DriveFolderPickerModalProps {
  isOpen: boolean;
  title?: string;
  onClose: () => void;
  onSelect: (folder: DriveFolder) => void;
}

type Breadcrumb = { id: string; name: string };

const DriveFolderPickerModal: React.FC<DriveFolderPickerModalProps> = ({
  isOpen,
  title = 'Choose a Google Drive folder',
  onClose,
  onSelect,
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([{ id: 'root', name: 'My Drive' }]);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = breadcrumbs[breadcrumbs.length - 1];

  const loadFolders = useCallback(async (parentId: string) => {
    setLoading(true);
    setError(null);
    try {
      await ensureGoogleDriveAuth();
      const items = await listDriveFolders(parentId);
      setFolders(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders.');
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setBreadcrumbs([{ id: 'root', name: 'My Drive' }]);
    setFolders([]);
    setError(null);
    void loadFolders('root');
  }, [isOpen, loadFolders]);

  if (!isOpen) return null;

  const openFolder = (folder: DriveFolder) => {
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    void loadFolders(folder.id);
  };

  const goToBreadcrumb = (index: number) => {
    const next = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(next);
    void loadFolders(next[next.length - 1].id);
  };

  const handleSelectCurrent = () => {
    if (current.id === 'root') {
      setError('Pick a folder inside My Drive (not the root).');
      return;
    }
    onSelect({ id: current.id, name: current.name });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full h-full sm:h-auto sm:max-w-lg sm:mx-4 bg-white dark:bg-slate-800 sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4" style={{ backgroundColor: '#15452f' }}>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <p className="text-emerald-100 text-sm mt-1">
            Navigate into a folder, then select it. Student files will be saved in subfolders named by student code.
          </p>
        </div>

        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
          <nav className="flex items-center gap-1 text-sm whitespace-nowrap">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={`${crumb.id}-${index}`}>
                {index > 0 && <span className="text-slate-400">/</span>}
                <button
                  type="button"
                  onClick={() => goToBreadcrumb(index)}
                  className={`px-1.5 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${
                    index === breadcrumbs.length - 1
                      ? 'font-semibold text-slate-900 dark:text-white'
                      : 'text-emerald-700 dark:text-emerald-400'
                  }`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-2 min-h-[220px]">
          {loading && (
            <p className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading folders…</p>
          )}
          {error && (
            <p className="m-2 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
              {error}
            </p>
          )}
          {!loading && !error && folders.length === 0 && (
            <p className="p-4 text-sm text-slate-500 dark:text-slate-400">No subfolders here.</p>
          )}
          {!loading &&
            folders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => openFolder(folder)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100"
              >
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                </svg>
                <span className="truncate text-sm font-medium">{folder.name}</span>
              </button>
            ))}
        </div>

        <div className="px-4 py-4 border-t border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-4 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSelectCurrent}
            disabled={current.id === 'root' || loading}
            className="flex-1 py-2.5 px-4 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Use “{current.name}”
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriveFolderPickerModal;
